import React from 'react';
import { useDispatch } from 'react-redux';
import {
    queueMapFitToExtent,
    resetLevel,
    stopFetchingCollection,
} from '../../../../state/_actions';
import { ColumnApi, GridApi } from 'ag-grid-community';
import PaneHeader from '../../../../components/PaneHeader';
import GridHeaderLeftControls from './components/GridHeaderLeftControls';
import GridHeaderRightControls from './components/GridHeaderRightControls';
import { useGridSourceSet } from '../../selectors/composed/index.hooks';
import {
    useGridCreatorLevel,
    useIsAnalyticsModeEnabled,
    useIsGridChart,
    useSourceSetId,
} from '../../selectors/index.hooks';
import SelectField from '../../../../components/SelectField';
import { default as LastStateIcon } from '@mui/icons-material/TrackChanges';
import { default as TasksIcon } from '@mui/icons-material/Assignment';
import { default as PoiIcon } from '@material-ui/icons/PinDrop';
import { default as RoutesIcon } from '@material-ui/icons/Timeline';
import { default as Bookmarks } from '@material-ui/icons/Bookmarks';
import { IDataGridCustomComponents } from '../DiscoveryGrid/DiscoveryGrid';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useSelectedGrid } from './hooks/useSelectedGrid';
import { getGridTitle } from './_utils/geGridTitle';

interface IOwnProps {
    gridApi: GridApi | undefined;
    gridColumnApi: ColumnApi | undefined;
    customComponents?: IDataGridCustomComponents | null;
    filter: string;
    handleChangeFilter: (filter: string) => void;
}

export const VALID_SOURCE_SET_IDS = [
    'lastStates',
    'routes',
    'tasks',
    'locations',
    'dataSegments',
];

export type ValidSourceSetId = typeof VALID_SOURCE_SET_IDS[number];

const DataGridPaneHeader = ({
    gridApi,
    gridColumnApi,
    customComponents,
    filter,
    handleChangeFilter,
}: IOwnProps) => {
    const dispatch = useDispatch();

    const isChart = useIsGridChart();
    const sourceSetId = useSourceSetId();
    const sourceSet = useGridSourceSet();
    const isAnalyticsEnabled = useIsAnalyticsModeEnabled();
    const creatorLevel = useGridCreatorLevel();

    const { selectedGrid, handleSelectDataSource } =
        useSelectedGrid(sourceSetId);

    const handleClearClick = () => {
        if (sourceSetId !== 'lastStates') {
            dispatch(stopFetchingCollection(sourceSetId || ''));
        }
        dispatch(resetLevel(creatorLevel));
        dispatch(queueMapFitToExtent());
    };

    const getRightCustomControls = () =>
        gridApi ? (
            <GridHeaderRightControls
                gridApi={gridApi}
                gridColumnApi={gridColumnApi}
                customComponents={customComponents}
            />
        ) : (
            <></>
        );

    const getLeftCustomControls = () => {
        if (!gridApi) {
            return <></>;
        }
        if (customComponents?.leftHeader) {
            return (
                <>
                    {customComponents.leftHeader(
                        sourceSet,
                        filter,
                        handleChangeFilter
                    )}
                </>
            );
        }
        return (
            <GridHeaderLeftControls
                showSearchField={!isChart}
                sourceSet={sourceSet}
                searchPhrase={filter}
                setGridSearch={handleChangeFilter}
            />
        );
    };
    return (
        <PaneHeader
            title={getGridTitle(sourceSet, isAnalyticsEnabled)}
            onCloseClick={handleClearClick}
            renderRightCustomControls={getRightCustomControls}
            renderLeftCustomControls={getLeftCustomControls}
            titleComponent={
                <SelectField
                    name="selectGrid"
                    value={selectedGrid}
                    ids={VALID_SOURCE_SET_IDS}
                    handleChange={handleSelectDataSource}
                    iconMap={{
                        lastStates: <LastStateIcon />,
                        tasks: <TasksIcon />,
                        locations: <PoiIcon />,
                        routes: <RoutesIcon />,
                        dataSegments: <Bookmarks />,
                        archiveEvents: <CalendarMonthIcon />,
                    }}
                />
            }
        />
    );
};

export default DataGridPaneHeader;
